import { useCallback, useEffect } from 'react';
import { useChatbot } from '../contexts/ChatbotContext';
import { supabase } from '../lib/supabase';
import { useSubscription } from './useSubscription';

interface RateLimits {
  [key: string]: {
    maxRequests: number;
    windowMinutes: number;
  };
}

const RATE_LIMITS: Record<string, RateLimits> = {
  free: {
    'chat': { maxRequests: 100, windowMinutes: 1 },
    'api': { maxRequests: 50, windowMinutes: 1 }
  },
  starter: {
    'chat': { maxRequests: 500, windowMinutes: 1 },
    'api': { maxRequests: 200, windowMinutes: 1 }
  },
  professional: {
    'chat': { maxRequests: 2000, windowMinutes: 1 },
    'api': { maxRequests: 1000, windowMinutes: 1 }
  },
  enterprise: {
    'chat': { maxRequests: 5000, windowMinutes: 1 },
    'api': { maxRequests: 2000, windowMinutes: 1 }
  }
};

export function useRateLimiting() {
  const { subscription, initializeSubscription, initialized } = useSubscription();
  const { chatbotUserId } = useChatbot();

  useEffect(() => {
    if (!initialized) {
      initializeSubscription(chatbotUserId ?? undefined);
    }
  }, [initialized, initializeSubscription, chatbotUserId]);

  const checkRateLimit = useCallback(async (endpoint: string) => {
    try {
      if (!subscription) {
        throw new Error('Subscription is not available');
      }
      // Check if chatbotUserId is available
      if (!chatbotUserId) {
        throw new Error('Chatbot user ID is not available');
      }
      const limits = RATE_LIMITS[subscription.tier || 'free'][endpoint] || RATE_LIMITS.free[endpoint];
      
      const { data, error } = await supabase.rpc('check_rate_limit', {
        p_user_id: chatbotUserId,
        p_endpoint: endpoint,
        p_max_requests: limits.maxRequests,
        p_window_minutes: limits.windowMinutes
      });
      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error checking rate limit:', err);
      return false;
    }
  }, [subscription, chatbotUserId]);

  return { checkRateLimit };
}